import { ActionCreator, ActionCreatorsMapObject, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import axios from 'axios';
import {
    IWorkspaceAction,
    IWorkspaceState,
} from '../../../common/model/WorkspaceModels';
import { showNotification } from '../../Notifications/NotificationActions';
import { sec } from '../../../security';
import config from '../../../auth.config';

const API_ROOT = process.env.REACT_APP_API_ROOT;

export const types = {
    GET_WORKSPACE_ACCESSES_LIST_REQUEST: 'GET_WORKSPACE_ACCESSES_LIST_REQUEST',
    GET_WORKSPACE_ACCESSES_LIST_SUCCESS: 'GET_WORKSPACE_ACCESSES_LIST_SUCCESS',
    GET_WORKSPACE_ACCESSES_LIST_FAILURE: 'GET_WORKSPACE_ACCESSES_LIST_FAILURE',
    GET_WORKSPACE_SECURITY_LIST_REQUEST: 'GET_WORKSPACE_SECURITY_LIST_REQUEST',
    GET_WORKSPACE_SECURITY_LIST_SUCCESS: 'GET_WORKSPACE_SECURITY_LIST_SUCCESS',
    GET_WORKSPACE_SECURITY_LIST_FAILURE: 'GET_WORKSPACE_SECURITY_LIST_FAILURE',
    GET_WORKSPACE_PENDING_LIST_REQUEST: 'GET_WORKSPACE_PENDING_LIST_REQUEST',
    GET_WORKSPACE_PENDING_LIST_SUCCESS: 'GET_WORKSPACE_PENDING_LIST_SUCCESS',
    INVITE_USER_REQUEST: 'INVITE_USER_REQUEST',
    INVITE_USER_SUCCESS: 'INVITE_USER_SUCCESS',
    INVITE_USER_FAILURE: 'INVITE_USER_FAILURE',
    CANCEL_INVITE_REQUEST: 'CANCEL_INVITE_REQUEST',
    CANCEL_INVITE_SUCCESS: 'CANCEL_INVITE_SUCCESS',
    CANCEL_INVITE_FAILURE: 'CANCEL_INVITE_FAILURE',
    REVOKE_ACCESS_REQUEST: 'REVOKE_ACCESS_REQUEST',
    REVOKE_ACCESS_SUCCESS: 'REVOKE_ACCESS_SUCCESS',
    REVOKE_ACCESS_FAILURE: 'REVOKE_ACCESS_FAILURE',
    CHANGE_ROLE_REQUEST: 'CHANGE_ROLE_REQUEST',
    CHANGE_ROLE_SUCCESS: 'CHANGE_ROLE_SUCCESS',
    CHANGE_ROLE_FAILURE: 'CHANGE_ROLE_FAILURE',
    GET_PENDING_INVITE_REQUEST: 'GET_PENDING_INVITE_REQUEST',
    GET_PENDING_INVITE_SUCCESS: 'GET_PENDING_INVITE_SUCCESS',
    GET_PENDING_INVITE_FAILURE: 'GET_PENDING_INVITE_FAILURE',
    POST_INVITATION_RESPONSE: 'POST_INVITATION_RESPONSE',
    POST_INVITATION_SUCCESS: 'POST_INVITATION_SUCCESS',
    POST_INVITATION_REJECT_SUCCESS: 'POST_INVITATION_REJECT_SUCCESS',
    POST_INVITATION_FAILURE: 'POST_INVITATION_FAILURE',
    DELETE_WORKSPACE_REQUEST: 'DELETE_WORKSPACE_REQUEST',
    DELETE_WORKSPACE_SUCCESS: 'DELETE_WORKSPACE_SUCCESS',
    DELETE_WORKSPACE_FAILURE: 'DELETE_WORKSPACE_FAILURE',
};

const getWorkspaceAccesses: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (
        workspaceId: string,
        limit: number,
        skip: number,
        sort: string,
        direction: string
    ) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });

        dispatch({
            type: types.GET_WORKSPACE_ACCESSES_LIST_REQUEST,
        });

        try {
            const response = await axios({
                method: 'GET',
                url: `${API_ROOT}/workspace/${workspaceId}/accesses`,
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json;charset=UTF-8',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + access_token,
                },
                params: {
                    workspaceId,
                    skip,
                    limit,
                    sort,
                    direction,
                },
            });

            dispatch({
                type: types.GET_WORKSPACE_ACCESSES_LIST_SUCCESS,
                payload: {
                    data: response.data,
                    error: undefined,
                },
            });
        } catch (error) {
            console.log('Get Workspace Access List Error', error);
            dispatch({
                type: types.GET_WORKSPACE_ACCESSES_LIST_FAILURE,
                error: 'There was an issue fetching the workspace access list. Please try again later.',
            });
        }
    };

const getWorkspaceSecurityByStatus: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (
        workspaceId: string,
        status: string,
        limit: number,
        skip: number,
        sort: string,
        direction: string
    ) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });

        if (status === 'PENDING') {
            dispatch({
                type: types.GET_WORKSPACE_PENDING_LIST_REQUEST,
            });
        } else {
            dispatch({
                type: types.GET_WORKSPACE_SECURITY_LIST_REQUEST,
            });
        }

        try {
            const response = await axios({
                method: 'GET',
                url: `${API_ROOT}/workspace/${workspaceId}/requests`,
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json;charset=UTF-8',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + access_token,
                },
                params: {
                    workspaceId,
                    status,
                    skip,
                    limit,
                    sort,
                    direction,
                },
            });

            if (status === 'PENDING') {
                dispatch({
                    type: types.GET_WORKSPACE_PENDING_LIST_SUCCESS,
                    payload: {
                        data: response.data,
                        // status,
                        error: undefined,
                    },
                });
            } else {
                dispatch({
                    type: types.GET_WORKSPACE_SECURITY_LIST_SUCCESS,
                    payload: {
                        data: response.data,
                        // status,
                        error: undefined,
                    },
                });
            }
        } catch (error) {
            console.log('Get Workspace Status List Error', error);
            dispatch({
                type: types.GET_WORKSPACE_SECURITY_LIST_FAILURE,
                error: 'There was an issue fetching the invite status. Please try again later.',
            });
        }
    };

const inviteUser: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (workspaceId: string, email: string, role: string) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });

        dispatch({
            type: types.INVITE_USER_REQUEST,
        });

        try {
            const response = await axios({
                method: 'POST',
                url: `${API_ROOT}/workspace/${workspaceId}/invite`,
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json;charset=UTF-8',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + access_token,
                },
                params: {
                    workspaceId,
                    email,
                    role,
                },
            });

            dispatch({
                type: types.INVITE_USER_SUCCESS,
                payload: {
                    data: response.data,
                    error: undefined,
                },
            });

            await dispatch(
                showNotification('User successfully invited', 'success')
            );
        } catch (error) {
            console.log('User Invite Error', error);
            dispatch({
                type: types.INVITE_USER_FAILURE,
                error: 'There was an issue creating dummy data for the workspace. Please try again later.',
            });
            await dispatch(
                showNotification(
                    'There was an issue inviting this user to the workspace. Please try again later.',
                    'error'
                )
            );
        }
    };

const cancelInvitation: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (id: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.CANCEL_INVITE_REQUEST,
    });

    try {
        await axios({
            method: 'DELETE',
            url: `${API_ROOT}/workspace/invitations/${id}/cancel`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.CANCEL_INVITE_SUCCESS,
            payload: {
                inviteId: id,
                error: undefined,
            },
        });

        await dispatch(
            showNotification('User invite successfully canceled', 'success')
        );
    } catch (error) {
        console.log('User invite cancel Error', error);
        dispatch({
            type: types.CANCEL_INVITE_FAILURE,
            error: 'There was an issue canceling this invitation. Please try again later.',
        });
        await dispatch(
            showNotification(
                'There was an issue canceling this invitation. Please try again later.',
                'error'
            )
        );
    }
};

const revokeAccess: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (workspaceId: string, userId: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.REVOKE_ACCESS_REQUEST,
    });

    try {
        const response = await axios({
            method: 'DELETE',
            url: `${API_ROOT}/workspace/workspace/${workspaceId}/revoke/${userId}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.REVOKE_ACCESS_SUCCESS,
            payload: {
                data: response.data,
                userId,
                error: undefined,
            },
        });

        await dispatch(
            showNotification('User access successfully revoked', 'success')
        );
    } catch (error) {
        console.log('User access revoke Error', error);
        dispatch({
            type: types.REVOKE_ACCESS_FAILURE,
            error: 'There was an issue revoking this users access. Please try again later.',
        });
        await dispatch(
            showNotification(
                'There was an issue revoking this users access. Please try again later.',
                'error'
            )
        );
    }
};

const changeRole: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (
        workspaceId: string,
        userEmail: string,
        newRole: string,
        ac_username: string
    ) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });

        dispatch({
            type: types.CHANGE_ROLE_REQUEST,
        });

        try {
            const response = await axios({
                method: 'PUT',
                url: `${API_ROOT}/workspace/${workspaceId}/change-role`,
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json;charset=UTF-8',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + access_token,
                    ac_username: ac_username,
                },
                params: {
                    userEmail,
                    newRole,
                },
            });

            dispatch({
                type: types.CHANGE_ROLE_SUCCESS,
                payload: {
                    data: response.data,
                    error: undefined,
                },
            });

            await dispatch(
                showNotification('User role successfully updated', 'success')
            );
        } catch (error) {
            console.log('User change role Error', error);
            dispatch({
                type: types.CHANGE_ROLE_FAILURE,
                error: 'There was an issue updating the user role. Please try again later.',
            });
            await dispatch(
                showNotification(
                    'There was an issue updating the user role. Please try again later.',
                    'error'
                )
            );
        }
    };

const getPendingInvitations: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (limit: number, skip: number, sort: string, direction: string) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });

        dispatch({
            type: types.GET_PENDING_INVITE_REQUEST,
        });

        try {
            const response = await axios({
                method: 'GET',
                url: `${API_ROOT}/workspace/invitations/pending`,
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json;charset=UTF-8',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + access_token,
                },
                params: {
                    skip,
                    limit,
                    sort,
                    direction,
                },
            });

            dispatch({
                type: types.GET_PENDING_INVITE_SUCCESS,
                payload: {
                    data: response.data,
                    error: undefined,
                },
            });
        } catch (error) {
            console.log('Get Workspace Access List Error', error);
            // @ts-ignore
            if (error && error.response && error.response.status === 404) {
                dispatch({
                    type: types.GET_PENDING_INVITE_SUCCESS,
                    payload: {
                        data: [],
                        error: undefined,
                    },
                });
            } else {
                dispatch({
                    type: types.GET_PENDING_INVITE_FAILURE,
                    error: 'There was an issue fetching the file import data. Please try again later.',
                });
            }
        }
    };

const invitationResponse: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (invitationId: string, status: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.POST_INVITATION_RESPONSE,
    });

    try {
        const response = await axios({
            method: 'POST',
            url: `${API_ROOT}/workspace/invitations/${invitationId}/${status}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        if (status === 'REJECTED') {
            await dispatch(
                showNotification(
                    'Workspace invite successfully rejected',
                    'success'
                )
            );
            dispatch({
                type: types.POST_INVITATION_REJECT_SUCCESS,
                payload: {
                    inviteId: invitationId,
                    error: undefined,
                },
            });
        } else {
            dispatch({
                type: types.POST_INVITATION_SUCCESS,
                payload: {
                    data: response.data,
                    error: undefined,
                },
            });
        }
        return true;
    } catch (error) {
        dispatch({
            type: types.GET_PENDING_INVITE_FAILURE,
            error: 'There was an issue fetching the file import data. Please try again later.',
        });
    }
};

const deleteWorkspace: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (id: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.DELETE_WORKSPACE_REQUEST,
    });

    try {
        await axios({
            method: 'DELETE',
            url: `${API_ROOT}/workspace/${id}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.DELETE_WORKSPACE_SUCCESS,
            payload: {
                workspaceId: id,
                error: undefined,
            },
        });

        await dispatch(
            showNotification('Workspace Successfully Deleted', 'success')
        );
        return true;
    } catch (error) {
        console.log('Workspace Delete Error', error);
        dispatch({
            type: types.DELETE_WORKSPACE_FAILURE,
            error: 'There was an issue deleting this workspace. Please try again later.',
        });
        await dispatch(
            showNotification(
                'There was an issue deleting this workspace. Please try again later.',
                'error'
            )
        );
    }
};

export const securityActions: ActionCreatorsMapObject<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = {
    getWorkspaceAccesses,
    getWorkspaceSecurityByStatus,
    inviteUser,
    cancelInvitation,
    revokeAccess,
    changeRole,
    getPendingInvitations,
    invitationResponse,
    deleteWorkspace,
};
